import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import { Loader } from '@components/Loader';
import { BaseCell } from '@account_module/components/users-table/BaseCell';
import { DetailsCell } from '@account_module/components/users-table/DetailsCell';
import { DetailsHeadCell } from '@account_module/components/users-table/DetailsHeadCell';
import { ActionCell } from '@account_module/components/users-table/ActionCell';
import { StatusCell } from '@account_module/components/users-table/StatusCell';
import { RoleCell } from '@account_module/components/users-table/RoleCell';
import { useOrganizations } from '@account_module/hooks/useOrganizations';
import { useOrganizationUsers } from '@account_module/hooks/useOrganizationUsers';
import {
  IOrganizationUser,
  IOrganizationTableUser,
  organizationUsersTableKeys,
  TableCellType,
  DetailCellType,
  ActionCellType,
  RoleCellType,
  UserRoleType,
  IGetTableColumns,
  StatusCellType,
} from '@account_module/types/Organizations';
import { reInviteUser } from '@account_module/services';
import { useAccountSettingContext } from '@account_module/context/AccountSettingsContext';
import { useNotification } from '@insights_module/hooks/useNotification';

const Row = tw.div`w-full px-6 grid grid-cols-5 items-center border-b border-solid border-gray-3 `;
const TD = tw.div`text-[11px] text-text-and-icon`;
const HeadTD = tw(TD)`font-semibold text-text-and-icon-muted-2`;

const COLUMN_KEY = {
  details: 'details' as organizationUsersTableKeys,
  email: 'email' as organizationUsersTableKeys,
  permision_type: 'permission' as organizationUsersTableKeys,
  status: 'status' as organizationUsersTableKeys,
  action: 'action' as organizationUsersTableKeys,
  permission: 'permission' as organizationUsersTableKeys,
};

export const UsersTable = () => {
  const { t } = useTranslation();
  const { NTFC_TYPE, notify } = useNotification();
  const { getOrganizationByIndex } = useOrganizations();
  const organization = getOrganizationByIndex(0);
  const [isActionCtxOpen, setIsActionCtxOpen] = useState<boolean>(false);
  const {
    data: users,
    currentUser,
    isLoading,
    mutateUser,
    isFetchingUsers,
    isMutateUserLoading,
  } = useOrganizationUsers(organization?.id);

  const {
    query,
    activeStatus,
    activeRole,
    selectedUsers,
    currentOrganization,
    selectedRow,
    setSelectedUsers,
    setIsRemoveUserModalActive,
    setSelectedRow,
  } = useAccountSettingContext();

  const expandedUsersData = useMemo(
    () =>
      users?.map(
        (user: IOrganizationUser): IOrganizationTableUser => ({
          ...user,
          details: user,
          action: user,
          permission: user,
        })
      ),
    [users]
  );

  const filteredData = useMemo(() => {
    if (query.length) {
      return expandedUsersData?.filter((item: IOrganizationTableUser) => {
        return (
          item.first_name.toLowerCase().startsWith(query.toLowerCase()) ||
          item.last_name.toLowerCase().startsWith(query.toLowerCase()) ||
          item.email.toLowerCase().startsWith(query.toLowerCase())
        );
      });
    }

    if (activeStatus === 'all' && activeRole === 'all') {
      return expandedUsersData;
    }

    if (activeStatus === 'all' && activeRole !== 'all') {
      return expandedUsersData?.filter((item: IOrganizationTableUser) => item.role === activeRole);
    }

    if (activeStatus !== 'all' && activeRole === 'all') {
      return expandedUsersData?.filter(
        (item: IOrganizationTableUser) => item.status === activeStatus
      );
    }

    if (activeRole !== 'all' && activeStatus !== 'all') {
      return expandedUsersData?.filter(
        (item: IOrganizationTableUser) => item.status === activeStatus && item.role === activeRole
      );
    }

    return expandedUsersData;
  }, [activeStatus, activeRole, expandedUsersData, query]);

  const handleUpdateUser = async (val: UserRoleType) => {
    if (isMutateUserLoading) return;

    mutateUser({ id: organization?.id, userId: selectedRow, payload: { permission_type: val } })
      .then(() => {
        notify({
          type: NTFC_TYPE.INFO,
          message: t('account_module.settings_page.users_table.notifications.user_updated_success'),
        });
      })
      .catch((error) =>
        notify({
          type: NTFC_TYPE.INFO,
          message: t('account_module.settings_page.users_table.notifications.user_updated_error', {
            message: error.message,
          }),
        })
      );
  };

  const resendInvite = async () => {
    const user = users?.find((u) => u.id === selectedRow);

    if (user) {
      reInviteUser(currentOrganization, selectedRow, {
        email: user.email,
        permission_type: user.role,
      })
        .then(() =>
          notify({
            type: NTFC_TYPE.INFO,
            message: t('account_module.settings_page.users_table.notifications.reinvite_success'),
          })
        )
        .catch((error) => notify({ type: NTFC_TYPE.INFO, message: `${error.message}` }));
    }
  };

  const handleCopyEmail = () => {
    notify({
      type: NTFC_TYPE.INFO,
      message: t('account_module.settings_page.users_table.notifications.email_copied_success'),
    });
  };

  const getTableColumns = ({
    users,
    currentUser,
    isLoading,
    isMutateUserLoading,
    selectedUsers,
    isFetchingUsers,
    isActionCtxOpen,
    resendInvite,
    setSelectedRow,
    handleUpdateUser,
    setIsActionCtxOpen,
    setIsRemoveUserModalActive,
    t,
  }: IGetTableColumns): TableCellType[] => {
    return [
      {
        key: COLUMN_KEY.details,
        header: (
          <DetailsHeadCell
            data={users as IOrganizationUser[]}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
          />
        ),
        columnRenderer: (item) =>
          DetailsCell({
            item,
            selectedUsers,
            setSelectedUsers,
            currentUser,
            setIsActionCtxOpen,
            setSelectedRow,
          } as DetailCellType),
        align: 'left',
      },
      {
        key: COLUMN_KEY.email,
        header: 'Email',
        columnRenderer: (item) => BaseCell(item as keyof IOrganizationTableUser),
        align: 'left',
      },
      {
        key: COLUMN_KEY.permission,
        header: 'Role',
        columnRenderer: (item) => {
          const getIsLoading = (item: IOrganizationTableUser) => {
            return (
              (isMutateUserLoading && selectedRow === item?.id) ||
              (isFetchingUsers && selectedUsers.includes(item?.id))
            );
          };

          return RoleCell({
            item,
            currentUser,
            selectedRow,
            isLoading: getIsLoading(item as IOrganizationTableUser),
            setSelectedRow,
            onChange: handleUpdateUser,
            setIsActionCtxOpen,
          } as RoleCellType);
        },
        align: 'left',
      },
      {
        key: COLUMN_KEY.status,
        header: 'Status',
        columnRenderer: (item) => StatusCell({ item, t } as StatusCellType),
        align: 'left',
      },
      {
        key: COLUMN_KEY.action,
        header: '',
        columnRenderer: (item) =>
          ActionCell({
            item,
            isLoading,
            selectedRow,
            currentUser,
            isActionCtxOpen,
            usersCount: users?.length || 0,
            setSelectedUsers,
            setSelectedRow,
            setIsActionCtxOpen,
            resendInvite,
            setIsRemoveUserModalActive,
            onCopyEmail: handleCopyEmail,
            t,
          } as ActionCellType),
        align: 'right',
      },
    ];
  };

  const _columns = useMemo(
    () =>
      getTableColumns({
        users,
        currentUser,
        isLoading,
        isMutateUserLoading,
        selectedUsers,
        currentOrganization,
        NTFC_TYPE,
        isFetchingUsers,
        isActionCtxOpen,
        resendInvite,
        setSelectedUsers,
        setIsRemoveUserModalActive,
        notify,
        handleUpdateUser,
        setIsActionCtxOpen,
        setSelectedRow,
        t,
      }),
    [
      users,
      currentUser,
      isLoading,
      isMutateUserLoading,
      selectedUsers,
      currentOrganization,
      NTFC_TYPE,
      organization,
      isFetchingUsers,
      resendInvite,
      setSelectedUsers,
      setIsRemoveUserModalActive,
      notify,
      handleUpdateUser,
      setIsActionCtxOpen,
      isActionCtxOpen,
      setSelectedRow,
      t,
    ]
  );

  if (isLoading) {
    return <Loader theme='dark' size='md' />;
  }

  if (!filteredData?.length && !isLoading) {
    return (
      <div tw='text-center text-text-and-icon w-full h-full flex items-center justify-center'>
        No data
      </div>
    );
  }

  return (
    <>
      <Row tw='bg-neutral-background sticky top-0 z-20'>
        {_columns.map((col) => (
          <HeadTD key={col?.key} tw='py-[10px]'>
            {col?.header}
          </HeadTD>
        ))}
      </Row>
      <div tw='h-[calc(100% - 41px)] overflow-y-scroll'>
        {filteredData?.map((user: IOrganizationTableUser) => (
          <Row key={user.id}>
            {_columns.map((col) => (
              <TD key={col.key} tw='py-[18px]' style={{ textAlign: col.align as never }}>
                {col.columnRenderer(user[col.key] as keyof IOrganizationTableUser)}
              </TD>
            ))}
          </Row>
        ))}
      </div>
    </>
  );
};
